// "What did enrichment do?" — a compact, always-visible summary that surfaces
// data the record already stores but the form mostly hid: the outcome (matched /
// no match / failed / still running), Gemini's confidence, which sources filled
// the fields, and a link to the source image it pulled. Addresses the "I'm blind
// to the result" gap (DESIGN §7). Pure read of existing fields — no backend work.

import type { Artifact, FieldSource } from "../types";

const SOURCE_LABELS: Record<FieldSource, string> = {
  caption: "caption",
  nga: "NGA",
  met: "Met",
  wikidata: "Wikidata",
  gemini: "Gemini",
  manual: "you",
};

const IMAGE_SOURCE_LABELS: Record<string, string> = {
  nga: "NGA",
  met: "the Met",
  wikidata: "Wikimedia",
  reverse: "reverse image search",
  original: "your photo",
};

/** Map the stored 0..1 confidence back to Gemini's high/medium/low buckets. */
function confidenceLabel(c: number | undefined): string | null {
  if (c == null) return null;
  if (c >= 0.8) return "high";
  if (c >= 0.5) return "medium";
  return "low";
}

type OutcomeKind = "match" | "nomatch" | "failed" | "pending";

function outcomeOf(a: Artifact): { kind: OutcomeKind; text: string } {
  const ran = a.updatedAt > a.createdAt; // enrichment wrote after the upload
  if (a.enrichmentError) return { kind: "failed", text: "Enrichment failed" };
  if (a.status === "pending" && !ran) {
    return { kind: "pending", text: "Enrichment hasn’t finished yet" };
  }
  if (a.imageFidelity === "online_match") {
    const src = a.imageSource ? IMAGE_SOURCE_LABELS[a.imageSource] ?? a.imageSource : null;
    return { kind: "match", text: src ? `Matched online · from ${src}` : "Matched online" };
  }
  return { kind: "nomatch", text: "Ran — no online match (kept your photo)" };
}

export function EnrichmentSummary({ artifact }: { artifact: Artifact }) {
  const outcome = outcomeOf(artifact);
  const conf = confidenceLabel(artifact.confidence);

  // Roll up per-field provenance into "N from <source>" badges.
  const counts = new Map<FieldSource, number>();
  for (const s of Object.values(artifact.fieldSources ?? {})) {
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const rollup = [...counts.entries()].sort((a, b) => b[1] - a[1]);

  return (
    <details className="enrich-summary" open>
      <summary className="enrich-summary__head">
        <span className={`enrich-summary__outcome enrich-summary__outcome--${outcome.kind}`}>
          {outcome.text}
        </span>
        {conf && (
          <span className="enrich-summary__conf" title="Gemini’s visual identification confidence">
            confidence: {conf}
          </span>
        )}
      </summary>

      <div className="enrich-summary__body">
        {rollup.length > 0 && (
          <div className="enrich-summary__row">
            <span className="enrich-summary__row-label">Fields from</span>
            <span className="enrich-summary__badges">
              {rollup.map(([src, n]) => (
                <span key={src} className={`source-badge source-badge--${src}`}>
                  {SOURCE_LABELS[src]} · {n}
                </span>
              ))}
            </span>
          </div>
        )}

        {artifact.captionText?.trim() && (
          <div className="enrich-summary__row">
            <span className="enrich-summary__row-label">Caption</span>
            <span className="enrich-summary__note">read from the page (authoritative)</span>
          </div>
        )}

        {(artifact.sourceImageUrl || artifact.ngaObjectId) && (
          <div className="enrich-summary__row">
            <span className="enrich-summary__row-label">Source</span>
            <span className="enrich-summary__links">
              {artifact.sourceImageUrl && (
                <a href={artifact.sourceImageUrl} target="_blank" rel="noreferrer">
                  view source image ↗
                </a>
              )}
              {artifact.ngaObjectId && (
                <span className="enrich-summary__note">NGA object {artifact.ngaObjectId}</span>
              )}
            </span>
          </div>
        )}

        {rollup.length === 0 && !artifact.captionText?.trim() && outcome.kind !== "pending" && (
          <p className="enrich-summary__note">
            No metadata was auto-filled — enter the details below by hand, or re-run enrichment.
          </p>
        )}
      </div>
    </details>
  );
}
